/**
 * Выбранный покупателем язык витрины — единственный источник правды,
 * читаемый ВНЕ React-дерева (#1362).
 *
 * Зачем отдельный модуль, а не только `I18nContext`. В embed-модели чат и
 * лендинг живут в РАЗНЫХ Shadow DOM'ах и, соответственно, в разных React-
 * деревьях: лендинг (`WidgetViewOverlays` → `MiniCarModal` → `CarLandingPage`)
 * обёрнут в `<I18nProvider>`, а чат (`registry/Chat` → `VoiceWidgetConnected`)
 * — нет. Через контекст виджет выбор языка не видит в принципе. Модуль же
 * один на бандл, поэтому он виден обоим деревьям, а localStorage добавляет
 * переживание перезагрузки.
 *
 * Ключевое различие, на котором всё держится:
 *
 *   • «язык страницы» = {явный выбор} ?? `DEFAULT_LANGUAGE` — всегда какой-то;
 *   • «явный выбор»   = только то, что покупатель ткнул в LanguageSelector.
 *
 * Виджет отправляет агенту ТОЛЬКО явный выбор. Просто монтирование провайдера
 * выбором не считается — иначе дилер без селектора языков (Andys) начал бы
 * слать агенту `fr` лишь потому, что где-то смонтировался I18nProvider.
 * Дефолт по тенанту — решение агента (`lc_graphs/prompts/language.py`), у него
 * для этого есть workflow_type, которого здесь нет.
 */

/** Whitelist UI-селектора (Autodis Luxembourg). RU/EL оператору не показываем. */
export const LANDING_LANGUAGES = ['fr', 'en', 'de', 'lu'];

/** Метаданные для UI (label = 2-letter код, title = human-readable). */
export const LANDING_LANGUAGE_META = {
  fr: { label: 'FR', title: 'Français' },
  en: { label: 'EN', title: 'English' },
  de: { label: 'DE', title: 'Deutsch' },
  lu: { label: 'LU', title: 'Lëtzebuergesch' },
};

/**
 * Дефолт витрины — французский (Autodis Luxembourg).
 *
 * Раньше здесь стоял `navigator.language`-автодетект. Его убрали намеренно:
 * язык страницы становился функцией настроек браузера, и «по умолчанию» на
 * разных устройствах означало разное — при том что дефолт диалога у агента
 * один. Детерминированный `fr` с обеих сторон, дальше — явный выбор.
 */
export const DEFAULT_LANGUAGE = 'fr';

/**
 * Ключ версионированный, и это обязательное условие корректности, а не вкусовщина.
 *
 * До #1362 `I18nProvider` писал в `b2b-demo:language` результат
 * `detectLanguage()` — то есть автодетект по `navigator.language` — на КАЖДОМ
 * монтировании. Значит у всякого, кто открывал витрину до выката, этот ключ уже
 * заполнен, и заполнен не выбором. Прочитать его по старому имени как «явный
 * выбор» означало бы: англоязычный браузер → сохранённый `en` → виджет шлёт
 * `language: 'en'` → агент открывает диалог по-английски. Ровно тот баг, ради
 * которого всё это написано, только теперь навсегда и незаметно на чистом
 * профиле.
 *
 * Новое имя = новый контракт. Легаси-значение не читаем никогда: тот, кто
 * действительно выбирал язык, выберет его ещё один раз, и это несравнимо
 * дешевле, чем пришпилить весь существующий трафик к автодетекту, который мы
 * только что убрали.
 */
const STORAGE_KEY = 'b2b-demo:language:v2';

/**
 * Явный выбор в пределах жизни страницы. Модульная переменная (а не только
 * localStorage) — чтобы выбор доезжал до чата и в private mode / iframe, где
 * storage бросает или молчит.
 *
 * Приоритет — за ней: `getSelectedLanguage` идёт в storage, только пока она
 * `null` (см. `explicitChoice ?? readStored()`). Выбор, сделанный на ЭТОЙ
 * странице, поэтому переживает и недоступный storage, и `localStorage.clear()`
 * из соседней вкладки. Обратная сторона: пока выбор сделан здесь, чужая запись
 * в storage не видна до перезагрузки — межвкладочную синхронизацию делает не
 * чтение, а `setSelectedLanguage` со своими подписчиками.
 */
let explicitChoice = null;

/** Подписчики на смену языка — провайдеры в разных Shadow DOM'ах. */
const listeners = new Set();

const normalize = (raw) =>
  typeof raw === 'string' && LANDING_LANGUAGES.includes(raw) ? raw : null;

const readStored = () => {
  if (typeof window === 'undefined') return null;
  try {
    return normalize(window.localStorage?.getItem(STORAGE_KEY));
  } catch {
    // private mode / iframe restrictions — выбора просто нет.
    return null;
  }
};

/**
 * Явный выбор покупателя, либо `null`, если он ничего не выбирал.
 *
 * `null` — значимое значение, а не «не знаю»: виджет по нему решает НЕ слать
 * `metadata.language` агенту и оставить выбор дефолта за тенантом.
 */
export const getSelectedLanguage = () => explicitChoice ?? readStored();

/**
 * Зафиксировать явный выбор. Возвращает нормализованный код либо `null`, если
 * язык вне whitelist (тогда ничего не меняется).
 */
export const setSelectedLanguage = (next) => {
  const language = normalize(next);
  if (!language) return null;
  explicitChoice = language;
  if (typeof window !== 'undefined') {
    try {
      window.localStorage?.setItem(STORAGE_KEY, language);
    } catch {
      // ignore — module-level выбор всё равно живёт до перезагрузки
    }
  }
  listeners.forEach((fn) => fn(language));
  return language;
};

/**
 * Подписаться на смену языка. Нужно потому, что `I18nProvider` может быть
 * смонтирован НЕСКОЛЬКО раз одновременно: `registry/Catalog` держит свой для
 * витрины, `WidgetViewOverlays` — свой для mini-car overlay поверх неё. Без
 * подписки выбор в overlay обновлял бы только его собственный провайдер, а
 * витрина под ним осталась бы на прежнем языке.
 */
export const subscribeToLanguage = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

/**
 * Язык, на котором рендерить страницу: явный выбор, иначе `fallback` (дефолт
 * экрана или язык сайта дилера — решает провайдер), иначе `DEFAULT_LANGUAGE`.
 * `fallback` вне whitelist игнорируется.
 */
export const resolveInitialLanguage = (fallback) =>
  getSelectedLanguage() ?? normalize(fallback) ?? DEFAULT_LANGUAGE;

/**
 * Язык страницы дилера — её `<html lang>`: andys-motors.com объявляет `en`,
 * autodis.lu — `fr` (#1469). Дефолт по дилеру без отдельной настройки: раньше
 * виджет на английском сайте Andys показывал витринный `fr` («En savoir plus
 * avec l'IA» на карточке машины). Явным выбором НЕ считается — агенту не
 * уходит, в storage не пишется.
 *
 * `en-GB` → `en`; `lb` (ISO-код люксембургского) → наш `lu`. Вне whitelist /
 * без атрибута → `null`.
 *
 * Смысл только на ЧУЖОЙ странице: в нашем приложении `<html lang>` — это наш
 * index.html. Поэтому решает провайдер, по widget-сборке.
 */
export const hostPageLanguage = () => {
  if (typeof document === 'undefined') return null;
  const raw = (document.documentElement.getAttribute('lang') || '').trim().toLowerCase();
  const base = raw.split(/[-_]/)[0];
  return normalize(base === 'lb' ? 'lu' : base);
};

/**
 * Проставить выбранный покупателем язык витрины в launch context (#1362).
 *
 * Живёт здесь, а не в виджете, по двум причинам: зависит только от
 * `getSelectedLanguage()` (никакого React), и вызывается из транспорта
 * (`useTextChat`), который сам импортируется виджетом — обратная зависимость
 * замкнула бы цикл.
 *
 * `null` (покупатель ничего не выбирал) означает «ключа не будет»: дефолт по
 * тенанту выбирает агент, у которого есть workflow_type. Готовый
 * `metadata.language` от вызывающего не перетираем.
 */
export const withSelectedLanguage = (launchContext) => {
  if (!launchContext || typeof launchContext !== 'object' || Array.isArray(launchContext)) {
    return launchContext;
  }
  const language = getSelectedLanguage();
  if (!language) return launchContext;
  const { metadata } = launchContext;
  // `Array.isArray` отдельно: `typeof [] === 'object'`, и спред массива дал бы
  // пустой объект, молча потеряв его содержимое.
  const base = metadata && typeof metadata === 'object' && !Array.isArray(metadata) ? metadata : {};
  // Присутствие ключа, а не truthiness: вызывающий, приславший `language: ''`,
  // сделал это осознанно, и перетирать его выбором из модуля мы не вправе.
  if ('language' in base) return launchContext;
  return { ...launchContext, metadata: { ...base, language } };
};

/**
 * Сбросить module-level состояние. ТОЛЬКО для тестов: между кейсами модуль
 * переиспользуется, и «выбор» протёк бы из предыдущего теста.
 */
export const __resetSelectedLanguageForTests = () => {
  explicitChoice = null;
  listeners.clear();
};
